import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaPlay } from 'react-icons/fa';
import gsap from 'gsap';

const Hero: React.FC = () => {
  const backgroundRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (backgroundRef.current) {
      // Create dynamic spotlight effect
      const moveSpotlight = (e: MouseEvent) => {
        const spotlight = backgroundRef.current;
        if (!spotlight) return;
        
        const rect = spotlight.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        gsap.to(spotlight, {
          background: `radial-gradient(circle at ${x}px ${y}px, rgba(234, 179, 8, 0.15) 0%, rgba(91, 33, 182, 0.1) 30%, rgba(0, 0, 0, 0) 70%)`,
          duration: 0.5
        });
      };
      
      window.addEventListener('mousemove', moveSpotlight);
      
      return () => {
        window.removeEventListener('mousemove', moveSpotlight);
      };
    }
  }, []);

  return (
    <section className="relative pt-16 pb-24 px-6 overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute w-full h-full object-cover"
          style={{ filter: 'brightness(0.4)' }}
        >
          <source src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/musicspanish//A_cinematic_ultrarealistic_202507031950_sk9.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay gradient for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
        
        {/* Enhanced Background Elements */}
        <div ref={backgroundRef} className="absolute inset-0">
          {/* Animated Spotlight */}
          <div className="absolute inset-0 bg-radial-gradient opacity-70"></div>
          
          {/* Animated Sound Waves */}
          <div className="absolute left-0 right-0 bottom-0 h-64 opacity-20">
            {[...Array(8)].map((_, i) => (
              <div 
                key={i}
                className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[200%] h-[200%] rounded-[40%] border border-yellow-500/10"
                style={{
                  animation: `ripple 8s infinite ease-in-out ${i * 0.5}s`,
                }}
              ></div>
            ))}
          </div>
          
          {/* Floating 3D Musical Notes */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-yellow-500/20 drop-shadow-[0_0_8px_rgba(234,179,8,0.3)]"
              initial={{ 
                x: Math.random() * 100 + '%', 
                y: Math.random() * 100 + '%',
                rotate: Math.random() * 360,
                scale: Math.random() * 0.5 + 0.5
              }}
              animate={{ 
                y: [null, '-20%'],
                rotate: [null, Math.random() * 360 + 180],
                opacity: [0.2, 0]
              }}
              transition={{ 
                duration: Math.random() * 10 + 15, 
                repeat: Infinity,
                ease: "linear"
              }}
              style={{ 
                fontSize: `${Math.random() * 3 + 2}rem`,
                filter: 'blur(0.5px)',
                textShadow: '0 0 10px rgba(234, 179, 8, 0.3)'
              }}
            >
              {['♪', '♫', '♬', '♩', '♭', '♮', '♯'][Math.floor(Math.random() * 7)]}
            </motion.div>
          ))}
          
          {/* Subtle Grid Pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col items-center text-center">
          {/* Tagline */}
          <motion.p
            className="text-yellow-400 font-medium mb-3 tracking-wider flex items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block w-8 h-[2px] bg-yellow-400 mr-3"></span>
            POWERED BY MUSIC · INSPIRED BY LANGUAGE
          </motion.p>
          
          {/* Main Headline */}
          <motion.h1 
            className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Learn Spanish with the 
            <span className="block mt-2 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 bg-clip-text text-transparent drop-shadow-[0_5px_20px_rgba(234,179,8,0.3)]">
              Power of Music
            </span>
          </motion.h1>
          
          {/* Description */}
          <motion.p 
            className="text-xl text-gray-300 mb-8 max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Master Spanish through your favorite songs with interactive exercises, 
            real-time feedback, and vocabulary practice that makes learning fun and effective.
          </motion.p>
          
          {/* CTAs */}
          <motion.div
            className="flex flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link 
              to="/register" 
              className="group inline-flex items-center bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-8 py-4 rounded-full font-bold text-lg hover:from-yellow-300 hover:to-yellow-400 transform hover:scale-105 transition-all duration-200 shadow-[0_5px_30px_rgba(234,179,8,0.3)]"
            >
              <span className="mr-2">Get Started Free</span>
              <span className="relative flex h-3 w-3 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-black"></span>
              </span>
              <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <a 
              href="#how-it-works" 
              className="inline-flex items-center px-6 py-4 border border-yellow-500/30 rounded-full text-yellow-400 hover:bg-yellow-500/10 transition-colors"
            >
              <FaPlay className="mr-2" />
              See How It Works
            </a>
          </motion.div>
          
          {/* Stats */}
          <motion.div 
            className="mt-12 flex flex-wrap justify-center gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            <div>
              <p className="text-3xl font-bold text-white">5k+</p>
              <p className="text-gray-400">Active Users</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">50+</p>
              <p className="text-gray-400">Ai Generated Songs</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">95%</p>
              <p className="text-gray-400">Success Rate</p>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Custom Styles */}
      <style jsx>{`
        @keyframes ripple {
          0% {
            transform: translate(-50%, 0) scale(0.3);
            opacity: 0.5;
          }
          100% {
            transform: translate(-50%, 0) scale(1);
            opacity: 0;
          }
        }
        
        .bg-radial-gradient {
          background: radial-gradient(circle at 50% 50%, rgba(234, 179, 8, 0.15) 0%, rgba(91, 33, 182, 0.1) 30%, rgba(0, 0, 0, 0) 70%);
        }
        
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 40px 40px;
        }
      `}</style>
    </section>
  );
};

export default Hero;